import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../api/apiClient';
import {
    Panel, PanelHeader, PanelTitle, ErrorNote, AdminButton, ButtonRow,
    TableWrap, Table, Th, Td, Tr, ModalOverlay, ModalPanel, ModalTitle, ModalFooter,
    Form, FormGroup, Label, TextInput, TextArea,
} from './adminStyles';
import { color, font } from '../../theme/tokens';

// --- Styled Components ---
const OptionInputGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 6px;
    .letter { width: 1.2em; font-family: ${font.mono}; font-size: 13px; color: ${color.text3}; }
    input[type='checkbox'] { flex-shrink: 0; width: 16px; height: 16px; margin: 0; accent-color: ${color.blood}; cursor: pointer; }
`;

const QuestionCell = styled(Td)`
    width: 50%;
    line-height: 1.6;
`;

// --- Component ---

function AdminQuestionBank() {
    // ... (Your existing hooks and logic remain exactly the same)
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    
    // 用于添加/编辑的模态框状态
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [formData, setFormData] = useState({
        category: '',
        question_text: '',
        type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answers: [],
    });

    const fetchQuestions = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await apiClient.get('/admin/questions');
            setQuestions(response.data);
        } catch (err) {
            setError('获取题目列表失败。');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleOpenModal = (question = null) => {
        if (question) {
            setEditingQuestion(question);
            setFormData({
                category: question.category,
                question_text: question.question_text,
                type: question.type,
                options: question.options.length < 4 ? [...question.options, ...Array(4 - question.options.length).fill('')] : question.options,
                correct_answers: Array.isArray(question.correct_answers) ? question.correct_answers : [],
            });
        } else {
            setEditingQuestion(null);
            setFormData({
                category: '',
                question_text: '',
                options: ['', '', '', ''],
                type: 'multiple_choice',
                correct_answers: [],
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingQuestion(null);
    };
    
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = value;
        setFormData(prev => ({ ...prev, options: newOptions }));
    };
    
    const handleAnswerChange = (index) => {
        setFormData(prev => ({
            ...prev,
            correct_answers: prev.correct_answers.includes(index)
                ? prev.correct_answers.filter(i => i !== index)
                : [...prev.correct_answers, index],
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Filter out empty options before submitting
        const payload = {
            ...formData,
            options: formData.options.filter(opt => opt.trim() !== ''),
        };
        const apiCall = editingQuestion
            ? apiClient.put(`/admin/questions/${editingQuestion.id}`, payload)
            : apiClient.post('/admin/questions', payload);
            
        try {
            await apiCall;
            fetchQuestions(); // Refresh list
            handleCloseModal();
        } catch (err) {
            setError(editingQuestion ? '更新题目失败' : '添加题目失败');
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('确定要删除这个题目吗？此操作不可逆转。')) {
            try {
                await apiClient.delete(`/admin/questions/${id}`);
                fetchQuestions(); // Refresh list
            } catch (err) {
                setError('删除题目失败');
                console.error(err);
            }
        }
    };

    const renderModal = () => (
        <ModalOverlay onClick={handleCloseModal}>
            <ModalPanel $wide role="dialog" aria-modal="true" aria-labelledby="question-modal-title" onClick={e => e.stopPropagation()}>
                <ModalTitle id="question-modal-title">{editingQuestion ? '编辑题目' : '添加新题目'}</ModalTitle>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="question-category">分类</Label>
                        <TextInput id="question-category" name="category" value={formData.category} onChange={handleFormChange} required placeholder="例如：动漫"/>
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="question-text">题干</Label>
                        <TextArea id="question-text" name="question_text" value={formData.question_text} onChange={handleFormChange} rows={3} required placeholder="请输入题目内容..." style={{ minHeight: 80 }}/>
                    </FormGroup>
                    <FormGroup role="group" aria-label="选项（勾选正确答案）">
                        <Label as="span">选项（勾选正确答案）</Label>
                        {formData.options.map((option, index) => (
                           <OptionInputGroup key={index}>
                                <input
                                    type="checkbox"
                                    checked={formData.correct_answers.includes(index)}
                                    onChange={() => handleAnswerChange(index)}
                                    aria-label={`选项 ${String.fromCharCode(65 + index)} 为正确答案`}
                                />
                                <span className="letter" aria-hidden="true">{String.fromCharCode(65 + index)}</span>
                                <TextInput value={option} onChange={(e) => handleOptionChange(index, e.target.value)} placeholder={`选项 ${String.fromCharCode(65 + index)}`} aria-label={`选项 ${String.fromCharCode(65 + index)}`} />
                            </OptionInputGroup>
                        ))}
                    </FormGroup>
                    <ModalFooter>
                         <AdminButton type="button" onClick={handleCloseModal}>取消</AdminButton>
                         <AdminButton type="submit" $variant="primary">保存</AdminButton>
                    </ModalFooter>
                </Form>
            </ModalPanel>
        </ModalOverlay>
    );

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>题库管理</PanelTitle>
                <AdminButton type="button" $variant="primary" onClick={() => handleOpenModal()}>
                    + 添加新题目
                </AdminButton>
            </PanelHeader>

            {isModalOpen && renderModal()}

            {error && <ErrorNote role="alert">{error}</ErrorNote>}

            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <Th>ID</Th>
                            <Th>分类</Th>
                            <Th style={{width: '50%'}}>题干</Th>
                            <Th>创建时间</Th>
                            <Th>操作</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><Td colSpan="5" $dim style={{ textAlign: 'center', letterSpacing: '0.2em' }}>载入中…</Td></tr>
                        ) : (
                            questions.map(q => (
                                <Tr key={q.id}>
                                    <Td $mono>{q.id}</Td>
                                    <Td>{q.category}</Td>
                                    <QuestionCell>{q.question_text.length > 70 ? `${q.question_text.substring(0, 70)}...` : q.question_text}</QuestionCell>
                                    <Td $mono>{new Date(q.created_at).toLocaleDateString()}</Td>
                                    <Td>
                                        <ButtonRow style={{ flexWrap: 'nowrap' }}>
                                            <AdminButton type="button" $small onClick={() => handleOpenModal(q)}>编辑</AdminButton>
                                            <AdminButton type="button" $small $variant="danger" onClick={() => handleDelete(q.id)}>删除</AdminButton>
                                        </ButtonRow>
                                    </Td>
                                </Tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </TableWrap>
        </Panel>
    );
}

export default AdminQuestionBank;
